import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiCheck, FiX } from 'react-icons/fi'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import Panel from '~/components/Panel'
import { Badge } from '~/components/UI/Badge'
import { formatDateToRequest, history, isPublisher } from '~/helpers'
import useModal from '~/hooks/useModal'
import useQueryParams from '~/hooks/useQuery'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import MediaReviewTable from '../_tables/MediaReviewTable'
import { getLabelAdType } from '../../dtos/common/campaign/format'
import { listAdsResultsV2Service } from '../../services/ad/result/v2'
import {
  handleStatusCampaign,
  handleStatusLocal
} from '../../store/grocery/campaign/actions'
import DestinationUrls from '../Campaign/DestinationUrls'
import { DestinationUrlValue } from '../Campaign/DestinationUrls/elements/Fields'
import { formatReviewDestinationUrls } from '../Campaign/DestinationUrls/helpers/formatReviewDestinationUrls'
import { Alerts } from './_components/Alerts'
import { ModalConfirmations } from './_components/Modal'
import { RejectMessage } from './_components/RejectMessage'

import * as S from './styles'

interface Props {
  status: CampaignStatus
  message: string
  adType: CampaignAdType
}

export const ReviewBar = ({ status, message, adType }: Props) => {
  const [contentModal, setContentModal] = useState<ContentModal | null>(null)
  const [submitError, setSubmitError] = useState(false)
  const [pendingDestinationUrls, setPendingDestinationUrls] = useState<
    DestinationUrlValue[] | null
  >(null)
  const [isDestinationUrlEnabled, setIsDestinationUrlEnabled] = useState(true)

  const [Modal, showConfirmationModal, toggleConfirmationModal, setIsShowing] =
    useModal()

  const dispatch = useDispatch()

  const { id: campaignId } = useParams()
  const activeCampaignIdRef = useRef(campaignId)
  const seenCampaignId = useRef<string | undefined>(undefined)
  const isMountedRef = useRef(true)

  activeCampaignIdRef.current = campaignId

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (seenCampaignId.current === undefined) {
      seenCampaignId.current = campaignId
      return
    }
    if (seenCampaignId.current === campaignId) return

    seenCampaignId.current = campaignId
    setPendingDestinationUrls(null)
    setIsDestinationUrlEnabled(true)
    setContentModal(null)
    setSubmitError(false)
    setIsShowing(false)
  }, [campaignId, setIsShowing])

  const query = useQueryParams()
  const callback_url = query.get('callback_url')
  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  const {
    campaign: {
      item: { data, loading: campaignLoading }
    },
    grocery: {
      campaign: {
        patch: { loading: loadingPatch }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const showEmbeddedDestinationUrls =
    !!data?.isSubpublisherReview && status === 'pending_review'

  // Until the panel seeds pending, the saved list can still contain a
  // destination the reviewer has not seen. Approve stays off in that window.
  const awaitingDestinationSeed =
    showEmbeddedDestinationUrls &&
    isDestinationUrlEnabled &&
    pendingDestinationUrls == null

  const modalDestinationUrls = useMemo(
    () => pendingDestinationUrls ?? [],
    [pendingDestinationUrls]
  )
  const shouldBlockApproval =
    awaitingDestinationSeed ||
    (showEmbeddedDestinationUrls &&
      isDestinationUrlEnabled &&
      formatReviewDestinationUrls(modalDestinationUrls).length === 0)

  // Consts

  const typeHasReview = [
    'banner',
    'banner_video',
    'sponsored_brand',
    'sponsored_brand_video',
    'digital_signage',
    'banner_off_site'
  ].includes(data?.adType)

  const showReviewActions = useMemo(
    () => isPublisher() && typeHasReview && status === 'pending_review',
    [status, typeHasReview]
  )

  // Scoped to sub-publisher review — an ordinary advertiser campaign
  // rejected for unrelated reasons must not gain this review header/message
  // just because it shares the same `rejected` status (review comment).
  const isRejected = useMemo(
    () => !!data?.isSubpublisherReview && status === 'rejected',
    [data?.isSubpublisherReview, status]
  )

  // Live total of ads still awaiting this sub-publisher's decision for this
  // ad type — decides whether the panel should show at all. Shares the
  // 'get-ads' query-key prefix with the per-item approve/reject mutations
  // below, so it stays fresh as soon as an item is reviewed, without
  // waiting for the campaign (Redux) data to be refetched.
  const params = useMemo(() => {
    const data = {
      campaign_id: campaignId,
      page: 1,
      quantity: 1,
      ad_type: adType,
      ad_status: 'pending_review',
      // Ainda não temos suporte para 2 queries no <Filters />
      account_info: false
    } as QueryParamsAds

    return data
  }, [campaignId, adType])

  const queryKey = ['get-ads', 'pending_review', 'single', campaignId]

  const { data: pendingReviewTotal } = useQuery({
    enabled: !!campaignId,
    queryKey,
    refetchInterval: 60000,
    queryFn: async () => {
      const response = await listAdsResultsV2Service({ params })
      return response?.data?.total ?? 0
    }
  })

  const showSection = useMemo(
    () =>
      showReviewActions ||
      !!pendingReviewTotal ||
      isRejected ||
      showEmbeddedDestinationUrls,
    [
      isRejected,
      pendingReviewTotal,
      showEmbeddedDestinationUrls,
      showReviewActions
    ]
  )
  const showMediaSection = showReviewActions || !!pendingReviewTotal

  // Approve modal media count: clicking "Aprovar" approves every ad of
  // this type in this sub-publisher's review scope that isn't rejected —
  // including ones already approved individually before, which no longer
  // show up under ad_status=pending_review. So the count is this scope's
  // total (no ad_status filter) minus the rejected ones, not
  // campaign.ads.length (which also counts ad sizes that never entered
  // this sub-publisher's review at all) and not pendingReviewTotal alone
  // (which misses already-approved items).
  const allAdsParams = useMemo(() => {
    const data = {
      campaign_id: campaignId,
      page: 1,
      quantity: 1,
      ad_type: adType,
      account_info: false
    } as QueryParamsAds

    return data
  }, [campaignId, adType])

  const { data: scopedAdsTotal } = useQuery({
    enabled: !!campaignId,
    queryKey: ['get-ads', 'all', 'single', campaignId],
    refetchInterval: 60000,
    queryFn: async () => {
      const response = await listAdsResultsV2Service({ params: allAdsParams })
      return response?.data?.total ?? 0
    }
  })

  const rejectedParams = useMemo(() => {
    const data = {
      campaign_id: campaignId,
      page: 1,
      quantity: 1,
      ad_type: adType,
      ad_status: 'rejected',
      account_info: false
    } as QueryParamsAds

    return data
  }, [campaignId, adType])

  const { data: rejectedAdsTotal } = useQuery({
    enabled: !!campaignId,
    queryKey: ['get-ads', 'rejected', 'single', campaignId],
    refetchInterval: 60000,
    queryFn: async () => {
      const response = await listAdsResultsV2Service({ params: rejectedParams })
      return response?.data?.total ?? 0
    }
  })

  // Handles

  const handleReject = () => {
    setSubmitError(false)
    setContentModal({
      titleModal: t('rm:reviewCampaign.reject.title.modal'),
      labelMessage: t('rm:reviewCampaign.reject.label.message'),
      subtitletLabelMessage: t(
        'rm:reviewCampaign.reject.subtitle.label.message'
      ),
      textButton: t('rm:button.Reject'),
      templateButton: 'critical',
      status: 'rejected',
      action: 'review',
      noteIsRequired: true,
      hasCancelButton: true
    })

    toggleConfirmationModal()
  }

  const handleApprove = () => {
    setSubmitError(false)
    // A sub-publisher fills in the destination URL(s) either in the
    // DestinationUrls panel or right here in the approve modal — the field
    // is required and the confirm button stays disabled until it's filled.
    // The network's own approval of its own campaign never goes through this
    // (data.isSubpublisherReview isn't set for it).
    setContentModal({
      titleModal: t('rm:reviewCampaign.approve.title.modal'),
      textButton: t('rm:button.Approve'),
      templateButton: 'primary',
      status: 'approved',
      action: 'review',
      hasCancelButton: true,
      editableDestinationUrls: !!data?.isSubpublisherReview,
      summary: {
        period: data?.endDate
          ? `${data.startDate} - ${data.endDate}`
          : `${data?.startDate} - ${t('rm:campaign.end_date.empty')}`,
        mediaLabel: getLabelAdType(data?.adType),
        mediaCount: t('rm:reviewCampaign.approve.summary.mediaCount', {
          count: Math.max((scopedAdsTotal ?? 0) - (rejectedAdsTotal ?? 0), 0)
        }),
        // A sub-publisher approves the network's own campaign — the daily
        // budget is the network's concern, not something this row ever
        // shows in that flow.
        dailyBudget: data?.isSubpublisherReview
          ? undefined
          : data?.budget?.total
      }
    })

    toggleConfirmationModal()
  }

  const queryClient = useQueryClient()

  const handleSubmit = useCallback(
    (dataForm: {
      status: CampaignStatus
      action: CampaignActions
      note: string
    }) => {
      setSubmitError(false)

      const shouldSendDestinationUrls =
        data?.isSubpublisherReview && dataForm.status === 'approved'

      const formattedDestinationUrls = isDestinationUrlEnabled
        ? formatReviewDestinationUrls(modalDestinationUrls)
        : []

      const body = {
        ...dataForm,
        ...(shouldSendDestinationUrls && {
          destination_urls: formattedDestinationUrls
        })
      }

      const callbackData = {
        status: dataForm?.status,
        status_history: [
          {
            status: dataForm?.status,
            note: dataForm?.note
          }
        ],
        ...(shouldSendDestinationUrls && {
          raw: {
            ...data?.raw,
            destination_urls: formattedDestinationUrls
          }
        })
      }

      const submittedCampaignId = data?.id || campaignId

      const callbackDispatch = () => {
        if (
          !isMountedRef.current ||
          activeCampaignIdRef.current !== submittedCampaignId
        ) {
          return
        }

        dispatch(handleStatusLocal(callbackData))

        queryClient.invalidateQueries({
          queryKey: ['get-ads']
        })

        setIsShowing(false)

        if (callback_url) {
          history.push(callback_url)
          return
        }

        if (dataForm.status !== 'approved') {
          return
        }

        dispatch(
          getCampaign({
            start_date: formatDateToRequest(startDate),
            end_date: formatDateToRequest(endDate),
            campaign_id: submittedCampaignId,
            account_info: true,
            requestInBackground: true,
            preserveOnError: true,
            expectedCampaignId: submittedCampaignId
          })
        )
      }

      const handleSubmitError = () => {
        if (
          !isMountedRef.current ||
          activeCampaignIdRef.current !== submittedCampaignId
        ) {
          return
        }

        setSubmitError(true)
        setContentModal(currentContent => {
          if (!currentContent) return currentContent

          return {
            ...currentContent,
            titleAlertMessage: t('rm:reviewCampaign.submit.error.title'),
            alertMessage: t('rm:reviewCampaign.submit.error.description')
          }
        })
      }

      dispatch(
        handleStatusCampaign({
          id: data?.id,
          body,
          onSuccess: callbackDispatch,
          onError: handleSubmitError
        })
      )
    },
    [
      callback_url,
      campaignId,
      data?.id,
      data?.isSubpublisherReview,
      data?.raw,
      dispatch,
      endDate,
      isDestinationUrlEnabled,
      modalDestinationUrls,
      queryClient,
      setIsShowing,
      startDate
    ]
  )

  const reviewButtons = (
    <S.WrapperInteractions>
      <Button
        template="critical"
        onClick={handleReject}
        loading={loadingPatch}
        text={t('rm:button.Reject')}
        iconLeft={<FiX />}
      />

      <Button
        template="primary"
        onClick={handleApprove}
        loading={loadingPatch}
        disabled={shouldBlockApproval}
        text={t('rm:button.Approve')}
        iconLeft={<FiCheck />}
      />
    </S.WrapperInteractions>
  )

  if (!showSection) return null

  return (
    <>
      <Panel
        className={status === 'pending_review' ? 'border-warning' : undefined}
      >
        <S.WrapperStatus data-testid="review-status-row">
          <S.TitleBlock>
            <S.TitleRow>
              <S.TitleStrong>{t('rm:CampaignReview')}</S.TitleStrong>
              {data?.targetingType === 'automatic' && (
                <Badge
                  size="tiny"
                  text={t('rm:automatic')}
                  data-testid="review-automatic-targeting"
                />
              )}
            </S.TitleRow>

            {showReviewActions && <Alerts status={status} />}
          </S.TitleBlock>

          {showReviewActions && reviewButtons}
        </S.WrapperStatus>

        {isRejected && <RejectMessage textMessage={message} />}
      </Panel>

      {showEmbeddedDestinationUrls && data && (
        <DestinationUrls
          key={campaignId}
          data={data}
          loading={!!campaignLoading}
          onPendingValueChange={setPendingDestinationUrls}
          pendingValue={pendingDestinationUrls}
          isDestinationUrlEnabled={isDestinationUrlEnabled}
          onDestinationUrlEnabledChange={setIsDestinationUrlEnabled}
        />
      )}

      {showMediaSection && (
        <Panel
          template="shoreline"
          title={t('rm:reviewCampaign.media.title')}
          description={t('rm:reviewCampaign.media.description')}
        >
          <MediaReviewTable />
        </Panel>
      )}

      <ModalConfirmations
        Modal={Modal}
        showConfirmationModal={showConfirmationModal}
        handleSubmit={handleSubmit}
        toggleConfirmationModal={toggleConfirmationModal}
        contentModal={contentModal}
        alertVariant={submitError ? 'critical' : 'warning'}
        destinationUrls={isDestinationUrlEnabled ? modalDestinationUrls : []}
        emptyDestinationUrl={
          !!data?.isSubpublisherReview && !isDestinationUrlEnabled
        }
      />
    </>
  )
}
